import type { AriaAttributes, FunctionComponent, ReactNode } from "react";
import type { Nil } from "cy-util";
import {
  ArrowLeftIcon,
  ArrowLongUpIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { cx } from "class-variance-authority";
import { NextLink } from "../Links/NextLink";
import { Header } from "../Heading/Header";
import { Button } from "../Buttons";
import { LoadingDots } from "../Loaders";
import { createBreakpointClassSelector } from "../../utils";
import { useBreakpoint } from "../../hooks";
import { TableSubMenu } from "./TableSubMenu";
import { tableUtils } from "./tableUtils";
import type { TableColumn, TableRow } from "./models";

function calculateHiddenColumnIndexes(columns: TableColumn[]) {
  const hiddenColumnIndexes: number[] = [];
  let hiddenColumnCount = 0;
  columns.forEach((col, index) => {
    if (hiddenColumnCount) {
      hiddenColumnIndexes.push(index);
      hiddenColumnCount--;
    } else if (col.headerColSpan) {
      hiddenColumnCount = col.headerColSpan - 1;
    }
  });
  return hiddenColumnIndexes;
}

const xCellPadding = "px-2 md:px-4";
const matchBreakpoint = createBreakpointClassSelector(
  "hidden",
  "sm:table-cell",
  "md:table-cell",
  "lg:table-cell",
  "xl:table-cell",
);

function renderColumn(
  columnInfo: TableColumn,
  index: number,
  hiddenColumnIndexes: number[],
) {
  if (hiddenColumnIndexes.includes(index)) return;

  return (
    <th
      className={cx(
        "py-2 font-semibold md:py-3",
        matchBreakpoint(columnInfo.showBreakpoint),
        xCellPadding,
        columnInfo.headerCellClassName,
      )}
      colSpan={columnInfo.headerColSpan || undefined}
      key={columnInfo.key}
      scope="col"
    >
      <span
        className={cx(
          columnInfo.headerValueClassName,
          columnInfo.smLabel && "hidden md:inline",
        )}
      >
        {columnInfo.label}
      </span>
      {columnInfo.smLabel ? (
        <span
          className={cx(
            columnInfo.headerValueClassName,
            columnInfo.smLabel && "inline md:hidden",
          )}
        >
          {columnInfo.smLabel}
        </span>
      ) : null}
    </th>
  );
}

export interface TableProps extends AriaAttributes {
  className?: string;
  columns: TableColumn[] | undefined;
  data?: TableRow[] | null;
  disableMobileView?: boolean;
  "data-testid"?: string;
}

/**
 *
 */
const TableRoot: FunctionComponent<TableProps> = ({
  columns,
  data,
  className,
  disableMobileView,
  "data-testid": dataTestId,
  ...rest
}) => {
  const { hasMinWidth } = useBreakpoint();
  const isMobileView = !hasMinWidth("sm");
  const testId = dataTestId || "table";

  if (!data || !columns)
    return (
      <div
        className="mt-8 grid place-content-center"
        data-testid="table-loading"
      >
        <LoadingDots height={20} />
      </div>
    );
  if (data.length === 0) {
    return (
      <div
        className={cx(
          "relative flex items-center justify-center overflow-x-auto rounded border border-gray-300 bg-white p-3 text-gray-700 italic",
        )}
        data-testid="table-empty"
      >
        No records found
      </div>
    );
  }
  if (isMobileView && !disableMobileView) {
    return renderList(className, data, columns, rest, testId);
  }

  return renderTable(className, data, columns, rest, testId);
};

function renderTable(
  className: string | Nil,
  data: TableRow[],
  columns: TableColumn[],
  props: any,
  testId: string,
) {
  const hiddenColumnIndexes = calculateHiddenColumnIndexes(columns);
  return (
    <div
      className={cx(
        "ring-opacity-5 relative overflow-x-auto rounded shadow outline outline-gray-200 md:rounded-lg",
        className,
      )}
      data-testid={testId}
    >
      <table
        {...props}
        className={cx(
          "w-full divide-y divide-gray-300 text-left text-sm text-gray-700",
        )}
        data-testid="table-element"
      >
        <thead
          className="sticky top-0 bg-gray-50 text-sm text-gray-900"
          data-testid="table-header"
        >
          <tr>
            {columns.map((col, colIndex) =>
              renderColumn(col, colIndex, hiddenColumnIndexes),
            )}
          </tr>
        </thead>
        <tbody
          className="divide-y divide-gray-200 text-gray-700"
          data-testid="table-body"
        >
          {data.map((row, index) => (
            <tr
              className={cx("bg-white", row.href && "hover:bg-slate-100")}
              key={index}
              data-testid={`table-row-${index}`}
            >
              {columns.map((col) => renderTableCell(row, col))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderList(
  className: string | Nil,
  data: TableRow[],
  columns: TableColumn[],
  props: any,
  testId: string,
) {
  return (
    <div
      {...props}
      className={cx(
        "relative flex flex-col gap-5 overflow-x-auto rounded bg-gray-50 text-gray-700",
        className,
      )}
      role="table"
      data-testid="table-mobile-view"
    >
      {data.map((row, index) => (
        <NextLink
          className={cx(
            "grid grid-cols-2 gap-4 border bg-gray-50 p-5",
            row.href && "hover:bg-slate-100",
          )}
          href={row.href}
          key={`${index}`}
          data-testid={`table-mobile-row-${index}`}
        >
          {columns.map((col) => (
            <div
              className={cx("flex flex-col flex-wrap gap-0.5")}
              key={col.key}
              data-testid={`table-mobile-cell-${col.key}`}
            >
              <div className={cx("mr-5 text-sm text-gray-500")}>
                {col.label}
              </div>
              <div className={cx("leading-none text-gray-800")}>
                {tableUtils.getDisplayValue(col, row)}
              </div>
            </div>
          ))}
        </NextLink>
      ))}
    </div>
  );
}

function renderTableCell(row: TableRow, col: TableColumn) {
  const Tag = col.isHeader ? "th" : "td";
  return (
    <Tag
      className={cx(col.rowCellClassName, matchBreakpoint(col.showBreakpoint))}
      key={col.key}
      data-testid={`table-cell-${col.key}`}
    >
      <NextLink
        className={cx(
          "block py-2 md:py-4",
          xCellPadding,
          col.rowContentCellClassName,
        )}
        href={row.href}
        data-testid={`table-cell-content-${col.key}`}
      >
        {tableUtils.getDisplayValue(col, row)}
      </NextLink>
    </Tag>
  );
}

export interface ContainerProps extends AriaAttributes {
  className?: string;
  children: ReactNode;
  "data-testid"?: string;
}

/**
 *
 */
const Container: FunctionComponent<ContainerProps> = ({
  className,
  children,
  ...rest
}) => {
  const css = cx(className, "grid gap-3");
  return (
    <div className={css} {...rest}>
      {children}
    </div>
  );
};

export interface TableHeaderProps extends AriaAttributes {
  className?: string;
  children: ReactNode;
}

/**
 *
 */
export const TableHeader: FunctionComponent<TableHeaderProps> = ({
  className,
  children,
  ...rest
}) => {
  const css = cx(className);
  return (
    <Header className={css} sizeVariant={5} {...rest}>
      {children}
    </Header>
  );
};

interface TablePaginationProps {
  className?: string;
  pageNumber: number;
  totalItems: number | Nil;
  onNextPage: (pageNo: number) => void;
  onPrevPage: (pageNo: number) => void;
  hasNext: boolean;
  hasPrevious: boolean;
  loading?: boolean;
}

/**
 *
 */
const TablePagination: FunctionComponent<TablePaginationProps> = ({
  className,
  totalItems,
  hasNext,
  hasPrevious,
  pageNumber,
  loading,
  onNextPage,
  onPrevPage,
}) => {
  if (!totalItems) {
    return null;
  }
  const css = cx(className, "flex gap-5 justify-end items-center");
  return (
    <div className={css} data-testid="table-pagination">
      <Button
        className="h-8 w-14"
        disabled={!hasPrevious || loading}
        onClick={() => {
          !loading && onPrevPage(pageNumber);
        }}
        type="button"
        vSize="sm"
        vColor="primary"
        data-testid="table-pagination-prev"
      >
        <ArrowLeftIcon className="h-5" />
      </Button>
      <div
        className={cx(
          "w-2 py-1 text-sm text-gray-500",
          !hasPrevious && "invisible",
        )}
        data-testid="table-pagination-prev-page"
      >
        {pageNumber - 1}
      </div>
      <div
        className="w-2 py-1 font-medium underline"
        data-testid="table-pagination-current-page"
      >
        {pageNumber}
      </div>
      <div
        className={cx(
          "w-2 py-1 text-sm text-gray-500",
          !hasNext && "invisible",
        )}
        data-testid="table-pagination-next-page"
      >
        {pageNumber + 1}
      </div>
      <Button
        className="h-8 w-14"
        disabled={!hasNext || loading}
        onClick={() => {
          onNextPage(pageNumber);
        }}
        type="button"
        vSize="sm"
        vColor="primary"
        data-testid="table-pagination-next"
      >
        <ArrowRightIcon className="h-5" />
      </Button>
    </div>
  );
};

interface CellLinkProps {
  className?: string;
  children: ReactNode;
  href: string;
  title?: string;
}

/**
 *
 */
const CellLink: FunctionComponent<CellLinkProps> = ({
  className,
  href,
  children,
  title,
}) => {
  const css = cx(className, "text-primary-500", "hover:text-primary-600");
  return (
    <NextLink
      className={cx(css)}
      href={href}
      title={title}
      data-testid="table-cell-link"
    >
      <span className={cx()}>{children}</span>
      <ArrowLongUpIcon className="relative -top-1 inline h-3 rotate-45 text-gray-500" />
    </NextLink>
  );
};

export const Table = Object.assign(TableRoot, {
  Container,
  Header: TableHeader,
  Pagination: TablePagination,
  CellLink,
  TableSubMenu,
});
